import { uploadData, getUrl, remove, copy } from 'aws-amplify/storage';
import { contentTypeForFileName, sanitizeAssetFileName, stripPublicPrefix } from './boothMap';

/**
 * Amplify Storage helpers for the hall plan and the booth files (plan image,
 * dimension PDF, 3D model). Objects are stored with accessLevel guest, i.e. at
 * "public/<key>"; the stored File is { fileName, s3Path: 'public/' + key, type }.
 */

export const PRODUCT_IMAGE_BASE_URL = 'https://iec-cp-public.s3.eu-central-1.amazonaws.com/ProductImages/';

/**
 * Uploads a file to public/<keyPrefix>/<timestamp>-<sanitized name>.
 *
 * @param {File} file
 * @param {{keyPrefix: string, contentType?: string, onProgress?: (percent: number) => void}} options
 * @returns {Promise<{fileName: string, s3Path: string, type: string}>}
 */
export async function uploadBoothAsset(file, { keyPrefix, contentType, onProgress } = {}) {
  if (!file) throw new Error('No file selected');
  const prefix = String(keyPrefix || 'boothAssets').replace(/^\/+|\/+$/g, '');
  const type = contentType || file.type || contentTypeForFileName(file.name);
  const key = `${prefix}/${Date.now()}-${sanitizeAssetFileName(file.name)}`;

  await uploadData({
    key,
    data: file,
    options: {
      accessLevel: 'guest',
      contentType: type,
      onProgress: ({ transferredBytes, totalBytes }) => {
        if (typeof onProgress !== 'function') return;
        const total = totalBytes || file.size;
        if (total) onProgress(Math.min(100, Math.round((transferredBytes / total) * 100)));
      },
    },
  }).result;

  return { fileName: file.name, s3Path: `public/${key}`, type };
}

// Signed URLs are reused while they are valid: every new signature is a new URL for the browser cache and for the
// 3D viewer's model cache, so switching back to a booth would download its 8-19 MB model again. A URL is handed
// out until 10 minutes before it expires (at least for half its lifetime).
const SIGNED_URL_RESERVE_MS = 10 * 60 * 1000;
const signedUrls = new Map(); // `${key}|${expiresIn}` -> { promise, url, validUntil }

function signedUrlEntry(s3Path, expiresIn) {
  const key = stripPublicPrefix(s3Path);
  if (!key) return { key: '', cacheKey: '', entry: null };
  const cacheKey = `${key}|${expiresIn}`;
  const entry = signedUrls.get(cacheKey);
  return { key, cacheKey, entry: entry && Date.now() < entry.validUntil ? entry : null };
}

/** Signed URL of a stored File (s3Path "public/..."); '' when there is no path. Reused while valid. */
export function getBoothAssetUrl(s3Path, { expiresIn = 3600 } = {}) {
  const { key, cacheKey, entry: cached } = signedUrlEntry(s3Path, expiresIn);
  if (!key) return Promise.resolve('');
  if (cached) return cached.promise;
  const lifetimeMs = expiresIn * 1000;
  const entry = { url: '', validUntil: Date.now() + Math.max(lifetimeMs - SIGNED_URL_RESERVE_MS, lifetimeMs / 2) };
  entry.promise = getUrl({ key, options: { expiresIn } }).then(
    (result) => {
      const url = result && result.url ? result.url.toString() : '';
      if (url) entry.url = url;
      else signedUrls.delete(cacheKey);
      return url;
    },
    (error) => {
      signedUrls.delete(cacheKey);
      throw error;
    }
  );
  signedUrls.set(cacheKey, entry);
  return entry.promise;
}

/** The signed URL getBoothAssetUrl already holds for a stored File, synchronously; '' when there is none (yet). */
export function peekBoothAssetUrl(s3Path, { expiresIn = 3600 } = {}) {
  const { entry } = signedUrlEntry(s3Path, expiresIn);
  return entry ? entry.url : '';
}

/**
 * Text content of a stored File (s3Path "public/...", e.g. the source SVG of a hall-plan version):
 * signed URL via getBoothAssetUrl, then fetch. Throws when there is no path or the response is not 2xx.
 */
export async function readBoothAssetText(s3Path) {
  const url = await getBoothAssetUrl(s3Path);
  if (!url) throw new Error('readBoothAssetText: no file path');
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`readBoothAssetText: the file ${stripPublicPrefix(s3Path)} could not be loaded (HTTP ${response.status})`);
  }
  return response.text();
}

/** Original file name of a stored key: last path segment without the "<timestamp>-" prefix of uploadBoothAsset. */
function fileNameOfKey(key) {
  const last = String(key || '').split('/').pop() || '';
  return last.replace(/^\d{10,}-/, '') || last;
}

/**
 * Copies a stored booth file (s3Path "public/...", e.g. the dimension PDF or GLB of a template product) into
 * the folder of another product: public/booth-assets/<targetProductId>/<timestamp>-<name>. Products never
 * share a file, because the booth editor deletes replaced files (a shared object would vanish from the
 * other product). Amplify Storage copy() within the bucket (accessLevel guest = "public/").
 *
 * @param {string} sourceS3Path s3Path of the stored File ("public/...")
 * @param {string} targetProductId id of the product that gets the copy
 * @param {{fileName?: string, type?: string}} [options] fileName / content type of the source File (default:
 *   derived from the key)
 * @returns {Promise<{fileName: string, s3Path: string, type: string}>} the File of the copy
 */
export async function copyBoothAsset(sourceS3Path, targetProductId, { fileName, type } = {}) {
  const sourceKey = stripPublicPrefix(sourceS3Path);
  if (!sourceKey) throw new Error('copyBoothAsset: no source file path');
  const productId = String(targetProductId || '').trim();
  if (!productId) throw new Error('copyBoothAsset: no target product');
  const name = String(fileName || '').trim() || fileNameOfKey(sourceKey);
  const key = `booth-assets/${productId}/${Date.now()}-${sanitizeAssetFileName(name)}`;
  await copy({ source: { key: sourceKey }, destination: { key } });
  return { fileName: name, s3Path: `public/${key}`, type: type || contentTypeForFileName(name) };
}

/** Deletes a stored File (s3Path "public/..."). */
export async function removeBoothAsset(s3Path) {
  const key = stripPublicPrefix(s3Path);
  if (!key) return;
  await remove({ key });
  for (const cacheKey of signedUrls.keys()) {
    if (cacheKey.startsWith(`${key}|`)) signedUrls.delete(cacheKey);
  }
}

function readFileAsText(file) {
  if (file && typeof file.text === 'function') return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error || new Error('Could not read the file'));
    reader.readAsText(file);
  });
}

/** Coordinate size of an SVG file: viewBox, else the width/height attributes ({width: null, height: null} if unknown). */
export async function readSvgDimensions(file) {
  const text = await readFileAsText(file);
  const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
  const root = doc.documentElement;
  if (!root || String(root.localName || root.tagName).toLowerCase() !== 'svg') {
    throw new Error('The file is not an SVG document');
  }
  const viewBox = root.getAttribute('viewBox');
  if (viewBox) {
    const parts = viewBox.trim().split(/[\s,]+/).map(Number);
    if (parts.length === 4 && parts.every(Number.isFinite) && parts[2] > 0 && parts[3] > 0) {
      return { width: parts[2], height: parts[3] };
    }
  }
  const width = parseFloat(root.getAttribute('width'));
  const height = parseFloat(root.getAttribute('height'));
  return {
    width: Number.isFinite(width) && width > 0 ? width : null,
    height: Number.isFinite(height) && height > 0 ? height : null,
  };
}

/** Natural pixel size of an image file (png/jpg). */
export function readImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('The image could not be read'));
    };
    image.src = objectUrl;
  });
}

/** Public URL of a product image (Product.image.main.fileName, uploaded via uploadCompanyImage). */
export function boothImageUrl(fileName) {
  if (typeof fileName !== 'string' || !fileName) return '';
  return `${PRODUCT_IMAGE_BASE_URL}${encodeURIComponent(fileName)}`;
}

// product images requested ahead of a click; kept referenced so the request is not dropped with the Image object
const preloadedImages = new Map();

/** Loads a product image into the browser cache (once per file), e.g. while the pointer rests on its booth. */
export function preloadBoothImage(fileName) {
  const url = boothImageUrl(fileName);
  if (!url || preloadedImages.has(url) || typeof Image === 'undefined') return;
  const image = new Image();
  image.decoding = 'async';
  image.src = url;
  preloadedImages.set(url, image);
}
